// The invite panel the shell header opens — the SHELL's layout of it.
//
// Nika 2026-10-09: "fix ui and layout of invite dropdown, make it clean,
// beautiful" + "make clear ux, imagine human using ... on daily basis". The
// admin who invites people every week: open → pick Email or Magic link → one
// action → a clear "Invited …" / "Link copied …" line.
//
// WHY A SHELL OVERRIDE. The panel's parts live in the shared library
// (shared/components/InviteDropdown.js, InviteFormInDropdown.js,
// MagicLinkInInvite.js, MembersInInvite.js — READ-ONLY here). The shell's
// components merge over the library's (context.js), so this `InviteDropdown`
// is the one the header's `openDropdown({ content: { extends:
// 'InviteDropdown' } })` mounts in the shell. It is a FULL definition, never
// `{ extends: 'InviteDropdown' }` (that resolves to itself once the shell's
// key replaces the library's, and renders with no children). The canvas
// MembersNavbar still mounts the library's panel.
//
// Every BEHAVIOUR stays in the shared parts — sending, the invite policy, the
// magic-link create / copy / revoke, the member list. This file only lays them
// out and restyles them through per-instance overrides on their bare keys
// (DOMQL deep-merges an instance object into the component it names); the
// children it ADDS land after the part's own children.
//
// THE SHAPE (top to bottom, one A gap, one A padding all round):
//   InviteHead      title + one short line
//   InviteMode      Email | Magic link — the kit's segmented control, ink
//                   thumb on a light track (only when a link may be shared)
//   InviteFormInDropdown   (Email) address · role + "Send invite" · the
//                   status line · the invite's link in a pill + Copy
//   MagicLinkInInvite      (Magic link) role + "Create and copy link" · the
//                   plain-words line (who joins, at which role, once, until
//                   when) · "Active links": role chip, ENDS date, SINGLE USE,
//                   Revoke with an in-place confirm · an empty state
//   NoInviteNote    why a caller cannot invite here
//   MembersInInvite the first members and "All members"
//
// Revoke never uses a browser dialog: the first press turns the row into
// "Revoke this link?" with Revoke (danger) and Keep. Keep takes the spot the
// first Revoke held (and gets focus), so a double click or a second fast
// Enter lands on the safe choice; both answers ignore activations for 300 ms
// after the swap. Only the confirm calls the server. Escape cancels the
// question without closing the panel. `revokeAsk` (this panel's state) names
// the row asking; `revokeAskAt` is when it asked.
//
// The status-line "Revoke" of the shared part is OFF here: the link just
// made is the first row of "Active links", with the same safe revoke. (This
// also carries the fix of shell branch feat/invite-link-join-unverified: that
// button showed before any link and after a revoke — `visibility` lost to the
// capsule's CSS; `hide` takes it out of the layout.)

export const InviteDropdown = {
  extends: 'MenuPanel',
  role: 'dialog',
  ariaLabel: (el) => el.call('polyglot', 'members.inviteDropdown.title'),

  // The shared form writes into THIS store (it owns no state), so every key
  // it writes is declared here, plus the panel's own three:
  //   mode       'email' | 'link' — the segmented control
  //   revokeAsk  the inviteId of the active-link row asking "Revoke this
  //              link?" ('' = none)
  //   revokeAskAt  when it asked (ms) — the confirm's double-activation guard
  state: {
    email: '',
    role: '',
    status: 'idle',
    error: '',
    failure: '',
    sentEmail: '',
    sentUrl: '',
    sentNoEmail: false,
    copied: false,
    mode: 'email',
    revokeAsk: '',
    revokeAskAt: 0
  },

  width: 'G2',
  padding: 'A',
  gap: 'A',
  textAlign: 'start',
  onClick: null,

  // Focus INTO the dialog, onto the email field (or the first link for a
  // caller with no form) — only when the dropdown layer hosts the panel.
  // Escape during "Revoke this link?" answers the question (Keep) and keeps
  // the panel open: the dropdown layer closes on an Escape nobody spent, so
  // this one is spent (preventDefault). Every other key keeps MenuPanel's
  // arrow-key model.
  onKeydown: (ev, el, s) => {
    if (ev.key === 'Escape' && s.revokeAsk) {
      ev.preventDefault()
      el.call('inviteLinkKeep', s.revokeAsk)
      return
    }
    el.call('menuArrowNav', ev, el.node)
  },

  onRender: (el) => {
    if (typeof el.lookup !== 'function' || !el.lookup('DropdownRoot')) return
    el.call('armMenuFocus', el, {
      selector: 'input[type="email"], a[href]',
      when: (node) => el.call('menuNodeVisible', node)
    })
  },

  InviteHead: {
    flow: 'y',
    gap: 'X2',
    Title: {
      tag: 'h2',
      margin: '0',
      fontSize: 'A',
      fontWeight: '500',
      lineHeight: '1.25',
      color: 'title',
      text: '{{ members.inviteDropdown.title | polyglot }}'
    },
    InviteSub: {
      tag: 'p',
      margin: '0',
      fontSize: 'Z1',
      color: 'caption',
      hide: (el, s) => !el.call('invitePolicy', s).mayInvite,
      text: (el, s) =>
        el.call(
          'polyglot',
          el.call('magicLinkOffered', s) ? 'shell.invite.sub' : 'shell.invite.subEmail'
        )
    }
  },

  // Email | Magic link. Full width, two equal segments.
  InviteMode: {
    extends: 'AppSegmented',
    variant: 'choice',
    width: '100%',
    ariaLabel: (el) => el.call('polyglot', 'shell.invite.mode.label'),
    hide: (el, s) => !el.call('magicLinkOffered', s),
    current: (el, s) => s.mode,
    select: 'inviteDropdownMode',
    // Each row carries the mode it was derived at: a change of `mode` re-derives
    // the rows, so every segment re-reads its own choice (a static row list
    // never re-evaluated `isActive` on a panel-state change).
    childrenAs: 'state',
    children: (el, s) => [
      { key: 'modeEmail', text: 'shell.invite.mode.email', value: 'email', mode: s.mode },
      { key: 'modeLink', text: 'shell.invite.mode.link', value: 'link', mode: s.mode }
    ],
    childProps: { flex: '1', isActive: (el, s) => !!s && s.value === s.mode }
  },

  // ── Email ────────────────────────────────────────────────────────────────
  InviteFormInDropdown: {
    gap: 'Z',
    hide: (el, s) =>
      !el.call('invitePolicy', s).mayInvite ||
      (s.mode === 'link' && el.call('magicLinkOffered', s)),

    // The row keeps the shared wrap law (a long Georgian role or label wraps
    // the button under the select; a control never clips its own label).
    Actions: {
      // The link moves to its own pill below (SentLink).
      CompactIconButton: { hide: () => true }
    },

    // One line keeps the main answer: "Invited ana@…", and after Copy
    // "Invited ana@… · Link copied" (the shared line swapped the whole
    // sentence for "Link copied").
    Note: {
      minHeight: 'auto',
      fontSize: 'Z1',
      text: (el, s) => {
        if (s.failure) return s.failure
        if (!s.sentEmail) return ''
        const key = s.sentNoEmail
          ? 'members.inviteDropdown.sentNoEmail'
          : 'members.inviteSent.person'
        const sent = el
          .call('polyglot', key)
          .replace('{who}', s.sentEmail)
          .replace('{email}', s.sentEmail)
        return s.copied
          ? sent + ' · ' + el.call('polyglot', 'members.inviteDropdown.linkCopied.title')
          : sent
      }
    },

    // The accept link of the invitation just sent, in a pill, with Copy.
    // A project invite has no link: no pill.
    SentLink: {
      flow: 'x',
      align: 'center',
      gap: 'Y',
      minWidth: '0',
      padding: 'X X X Z1',
      round: 'radiusPill',
      theme: 'segmentTrack',
      hide: (el, s) => !s.sentUrl,
      SentLinkText: {
        tag: 'span',
        flex: '1',
        minWidth: '0',
        fontFamily: 'Code',
        fontSize: 'Z',
        color: 'caption',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        text: (el, s) => s.sentUrl || ''
      },
      CompactIconButton: {
        icon: (el, s) => (s.copied ? 'check' : 'copy outline'),
        aria: {
          label: (el, s) =>
            el.call(
              'polyglot',
              s.copied
                ? 'members.inviteDropdown.linkCopied.title'
                : 'members.inviteDropdown.copyLink'
            )
        },
        title: (el, s) =>
          el.call(
            'polyglot',
            s.copied ? 'members.inviteDropdown.linkCopied.title' : 'members.inviteDropdown.copyLink'
          ),
        onClick: (ev, el, s) => {
          ev.preventDefault()
          if (!s.sentUrl) return
          el.call('copyStringToClipboard', s.sentUrl)
          s.update({ copied: true })
        }
      }
    }
  },

  // ── Magic link ───────────────────────────────────────────────────────────
  // The section owns its state; `s.parent` is this panel's.
  MagicLinkInInvite: {
    gap: 'Z',
    hide: (el, s) => !el.call('magicLinkOffered', s) || (s.parent && s.parent.mode) !== 'link',

    // The mode switch names the section.
    CaptionTitle_link: { hide: () => true },

    LinkActions: {
      // In this mode the link is the ONE action: the primary capsule below
      // replaces the shared secondary one (same handler, same busy label).
      AppCapsuleBtnToolbar: { hide: () => true },
      AppCapsuleBtnPrimary: {
        flexShrink: '0',
        disabled: (el, s) => s.status === 'creating',
        ariaBusy: (el, s) => (s.status === 'creating' ? 'true' : null),
        AppLabelStack: {
          labels: ['shell.invite.link.create', 'members.inviteDropdown.magicLink.creating'],
          shown: (el, s) => (s.status === 'creating' ? 1 : 0)
        },
        onClick: (ev, el, s) => {
          ev.preventDefault()
          const host = el.lookup('MagicLinkInInvite')
          if (host) host.scope.magicLinkCreate(host, s)
        }
      }
    },

    // The plain-words line BEFORE a link exists (role, once, days) and the
    // "Link copied …" result after — the shared text, in a readable size.
    LinkStatusRow: {
      LinkStatus: {
        LinkStatusText: {
          fontSize: 'Z1',
          color: (el, s) => (s.failure ? 'dangerInk' : s.createdId ? 'title' : 'caption')
        },
        LinkHintSizer: { fontSize: 'Z1' },
        LinkResultSizer: { fontSize: 'Z1' }
      },
      AppCapsuleBtnGhost: { hide: () => true }
    },

    ActiveLinks: {
      gap: 'Y',
      // Once loaded the list always shows — rows, or the empty line.
      hide: (el, s) => s.linksStatus !== 'ready' && s.linksStatus !== 'error',

      CaptionTitle_active: {},

      LinkList: {
        gap: 'X',
        childProps: {
          gap: 'Y',
          padding: 'X 0',
          round: 'radiusControl',
          // Quiet rows: the role chip carries the fill; the asking row reads by
          // its words and its red Revoke. The row's own words are its chips now.
          LinkWhen: { hide: () => true },

          // Asking row: Revoke this link? ........ Revoke  Keep
          // Normal row (two lines by design, never by wrap):
          //   [ROLE]                                  Revoke
          //   ENDS OCT 16 · SINGLE USE
          LinkInfo: {
            flow: 'y',
            align: 'start',
            gap: 'X',
            flex: '1',
            minWidth: '0',
            hide: (el, s) => el.call('inviteLinkRowAsking', s),
            AppStatusChip_role: {
              color: 'title',
              Dot: { hide: () => true },
              label: (el, s) => el.call('inviteLinkRowRole', s)
            },
            // One line at any width: the meta has the row to itself, and
            // the ellipsis guards a language longer than the room.
            AppMicroLabel_meta: {
              maxWidth: '100%',
              color: 'caption',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              text: (el, s) =>
                el
                  .call('polyglot', 'shell.invite.link.ends')
                  .replace('{date}', el.call('inviteLinkRowDate', s)) +
                ' · ' +
                el.call('polyglot', 'shell.invite.link.singleUse')
            }
          },
          LinkAsk: {
            tag: 'span',
            flex: '1',
            minWidth: '0',
            fontSize: 'Z1',
            fontWeight: '500',
            color: 'title',
            hide: (el, s) => !el.call('inviteLinkRowAsking', s),
            text: (el) => el.call('polyglot', 'shell.invite.link.revokeAsk')
          },
          // Asking: [Revoke this link?] [Revoke (danger)] [Keep] — Keep is the
          // LAST button, the spot the first Revoke held.
          AppCapsuleBtnGhost_confirm: {
            compact: true,
            flexShrink: '0',
            color: 'dangerInk',
            hide: (el, s) => !el.call('inviteLinkRowAsking', s),
            aria: {
              label: (el, s) =>
                el
                  .call('polyglot', 'shell.invite.link.revokeConfirmLabel')
                  .replace('{role}', el.call('inviteLinkRowRole', s))
                  .replace('{date}', el.call('inviteLinkRowDate', s))
            },
            AppLabelStack: {
              labels: [
                'members.inviteDropdown.magicLink.revoke',
                'members.inviteDropdown.magicLink.revoking'
              ],
              shown: (el, s) => (s.busy ? 1 : 0)
            },
            onClick: (ev, el, s) => {
              ev.preventDefault()
              if (el.call('inviteLinkGuarded')) return
              el.call('inviteLinkConfirm', s)
            }
          },
          AppCapsuleBtnGhost_keep: {
            compact: true,
            flexShrink: '0',
            hide: (el, s) => !el.call('inviteLinkRowAsking', s),
            text: '{{ shell.invite.link.keep | polyglot }}',
            onClick: (ev, el, s) => {
              ev.preventDefault()
              if (el.call('inviteLinkGuarded')) return
              el.call('inviteLinkKeep', s.inviteId)
            }
          },
          // The shared row button gives way to AppCapsuleBtnGhost_revoke.
          AppCapsuleBtnGhost: { hide: () => true },
          // Not asking: [ROLE / meta] [Revoke]. The first press only asks; it
          // also shows "Revoking…" while the confirmed revoke runs.
          AppCapsuleBtnGhost_revoke: {
            compact: true,
            flexShrink: '0',
            color: 'caption',
            hide: (el, s) => el.call('inviteLinkRowAsking', s),
            disabled: (el, s) => !!s.busy,
            ariaBusy: (el, s) => (s.busy ? 'true' : null),
            aria: {
              label: (el, s) =>
                el
                  .call('polyglot', 'members.inviteDropdown.magicLink.revokeLabel')
                  .replace('{role}', el.call('inviteLinkRowRole', s))
                  .replace('{date}', el.call('inviteLinkRowDate', s))
            },
            AppLabelStack: {
              labels: [
                'members.inviteDropdown.magicLink.revoke',
                'members.inviteDropdown.magicLink.revoking'
              ],
              shown: (el, s) => (s.busy ? 1 : 0)
            },
            onClick: (ev, el, s) => {
              ev.preventDefault()
              el.call('inviteLinkAsk', s)
            }
          }
        }
      },

      LinkEmpty: {
        tag: 'p',
        margin: '0',
        fontSize: 'Z1',
        color: 'caption',
        hide: (el, s) => s.linksStatus !== 'ready' || (s.links || []).length > 0,
        text: (el) => el.call('polyglot', 'shell.invite.link.empty')
      }
    }
  },

  // A caller the form is hidden from learns why, in one line.
  NoInviteNote: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z1',
    color: 'caption',
    hide: (el, s) => !el.call('invitePolicy', s).reason,
    text: (el, s) => {
      const reason = el.call('invitePolicy', s).reason
      if (!reason) return ''
      return el.call(
        'polyglot',
        reason === 'personalOrg'
          ? 'members.inviteDropdown.personalOrg'
          : 'members.inviteDropdown.noPermission'
      )
    }
  },

  MembersInInvite: {}
}
